import NetxQueryClient, {
	type QueryPager,
	type SuccessResponse,
} from '@netx/core-next/lib/queryClient';
export type {QueryError} from '@netx/core-next/lib/queryClient';
import {getResponseResults} from '@netx/core-next/utils/api';
import {
	getAttributeVocabularyDefaultSort,
	type IAttributeVocabulary,
	type AttributeVocabularySort,
	type AttributeVocabularyFilter,
	getAttributeVocabularyDefaultFilters,
} from '../schema';
import {ATTRIBUTE_VOCABULARY_PAGE_SIZE} from '../constants';

/**
 * `getAttributeVocabularies` request params
 * (what is used to send to server)
 */

export type GetAttributeVocabulariesRequestOptions = {
	filters?: Partial<AttributeVocabularyFilter>;
	sort?: Partial<AttributeVocabularySort>;
	startIndex?: number;
	stopIndex?: number;
};

/**
 * `getAttributeVocabularies` request
 * (what gets sent to the server)
 */
export type GetAttributeVocabulariesRequest = [
	string,
	AttributeVocabularySort,
	QueryPager,
];

/**
 * `getAttributeVocabularies` response
 * (what comes back from the server)
 */
export type GetAttributeVocabulariesResponse =
	SuccessResponse<GetAttributeVocabulariesResult>;

/**
 * `getAttributeVocabularies` result
 * (what is utilized from the server response)
 */
export type GetAttributeVocabulariesResult = {
	results: IAttributeVocabulary[];
	size: number;
	startIndex: number;
};

export const getAttributeVocabulariesEndpoint = 'getAttributeVocabularies';

/**
 * Get attribute vocabularies list
 * @param fetchParams Fetch params
 * @param signal Abort signal

 */
export const getAttributeVocabularies = async (
	{
		filters,
		sort,
		startIndex = 0,
		stopIndex = ATTRIBUTE_VOCABULARY_PAGE_SIZE,
	}: GetAttributeVocabulariesRequestOptions,
	signal?: AbortSignal,
): Promise<GetAttributeVocabulariesResult> => {
	const client = NetxQueryClient();

	const requestParams: GetAttributeVocabulariesRequest = [
		getAttributeVocabularyDefaultFilters({filters}).name ?? '',
		getAttributeVocabularyDefaultSort(sort),
		{startIndex, size: stopIndex - startIndex},
	];

	return client
		.post<GetAttributeVocabulariesResponse>(
			getAttributeVocabulariesEndpoint,
			requestParams,
			{
				signal,
			},
		)
		.then(getResponseResults);
};

export default getAttributeVocabularies;
